/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import { IGrammarItem, IGrammarStage, IGrammarTopic } from '@datatype/iyagigo/type';
import { EApiLanguageCode, IClassroomGrammarWorkbookData } from '@datatype/iyagigo/api-type';
import { useClassroomGrammarWorkbooksQuery } from '@hook/iyagigo/useApi';
import { groupClassroomGrammars } from '@util/classroom';
import TextBlock from '@component/common/block/text-block';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import ImageBlock from '@component/common/block/image-block';
import Chip from '@design-system/chip';
import ProgressBar from '@design-system/progress-bar';
import TextButton from '@design-system/text-button';
import Gap from '@component/common/gap';
import otterTestImage from '@asset/image/otter-test.webp';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import { IYAGIGO_PATH } from '@constant/iyagigo/route-path';
import { v4 as uuid4 } from 'uuid';

// ###################################
// GrammarHome — 기초문법 목차 (단계 > 주제 > 문법)
// - classroom-home의 '문법' 탭에서 렌더 (character-home과 형제)
// - 상단 '현재 단계' 배너 → 클릭 시 단계 선택 모달(글로벌: grammar-stage-select). 선택 단계의 주제→문법만 아래 노출
//   선택 단계는 classroom-slice(currentGrammarStageKey)를 구독
// - 주제 단위 아코디언: 제목 클릭 시 문법 카드 리스트 펼침/접힘
// - 문법 클릭 → 학습 화면(/ig/classroom-grammar)
// - 백엔드 classroom grammar 응답을 단계·주제 계층으로 조립해 렌더
// ###################################

// 단계 선택 배너 (cloze-home '현재 난이도' 배너 패턴 미러)
const BannerContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: ${size.MARGIN_2};
  width: 100%;
`;

const BannerContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 8px;
`;

const TodayStudyContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${size.MARGIN_1};
  border-radius: ${size.BORDER_RADIUS_2};
  background-color: ${color.WHITE_1};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const ProgressLabelContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

// 주제 섹션들 세로 나열 (섹션 간 간격)
const TopicCardsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${size.MARGIN_2};
`;

// 주제 한 섹션 = 카드 밖 라벨 + 흰 카드(문법 리스트)
const TopicSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: 5px;
`;

// 문법 리스트 카드 (흰 카드)
const GrammarsContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  border-radius: ${size.BORDER_RADIUS_2};
  border: 1px solid ${color.GRAY_BOX_BORDER_COLOR};
`;

const GrammarCardContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${size.MARGIN_1};
  cursor: pointer;

  &:not(:last-of-type) {
    border-bottom: 1px solid ${color.GRAY_4};
  }
`;

const GrammarLabelContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const GrammarKeywordContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  //gap: 5px;
`;

// 주제 한 섹션 — 카드 밖 라벨(주제명+개수) + 흰 카드(문법 리스트). 항상 펼침(아코디언 없음).
const TopicCard: React.FC<{
  topic: IGrammarTopic;
  workbooks: IClassroomGrammarWorkbookData[];
  onGrammarClick: (grammar: IGrammarItem) => void;
}> = ({ topic, workbooks, onGrammarClick }) => {
  const Grammars = topic.grammars.map((grammar) => {
    const workbook = workbooks.find((item) => item.classroomGrammar.grammarKey === grammar.grammarKey);
    return (
      <GrammarCardContainer key={uuid4()} onClick={() => onGrammarClick(grammar)}>
        <GrammarKeywordContainer>
          {/* label(제목)만 노출 — form(형태)은 학습 화면에서 크게 표기 */}
          <GrammarLabelContainer>
            <TextBlock
              text={grammar.grammarName?.ko ?? ''}
              design={{ text: { size: 'medium', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
            />
            {!!workbook?.studiedAt && (
              <Chip text='학습 완료' size='tiny' usage='static' kind='outlined' theme={ETheme.GREEN} />
            )}
          </GrammarLabelContainer>
        </GrammarKeywordContainer>
        <IconBlock icon='right' design={{ icon: { size: 'small', color: color.GRAY_1, kind: 'outlined' }, box: {} }} />
      </GrammarCardContainer>
    );
  });

  return (
    <TopicSection>
      <TextBlock
        text={topic.name?.ko ?? ''}
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <GrammarsContainer>{Grammars}</GrammarsContainer>
    </TopicSection>
  );
};

const GrammarHome: React.FC<any> = () => {
  const dispatch = useTypedDispatch();
  const languageCode =
    useTypedSelector((state) => state.rootReducer?.studentReducer?.currentStudyLanguage) ?? EApiLanguageCode.JAPANESE;
  const navigate = useNavigate();
  const { data: workbookResponse, isLoading: isWorkbookLoading } = useClassroomGrammarWorkbooksQuery(
    languageCode,
  );
  const workbooks: IClassroomGrammarWorkbookData[] = workbookResponse?.data ?? [];
  const stages = groupClassroomGrammars(workbooks.map((workbook) => workbook.classroomGrammar));

  // 선택된 단계 — classroom-slice 구독 (null이면 첫 단계 기본). 단계 선택 모달은 글로벌(grammar-stage-select)
  const selectedStageKey =
    useTypedSelector((state) => state.rootReducer?.classroomReducer?.currentGrammarStageKey) ?? null;
  const selectedStage: IGrammarStage | null =
    stages.find((stage) => stage.key === selectedStageKey) ?? stages[0] ?? null;

  // 배너 표기용 — title이 순수 주제명(예: '형용사'). 'N단계'는 order로 별도 조합(모달).
  const selectedStageLabel = selectedStage?.name?.ko ?? '';

  const selectedStageWorkbooks = workbooks.filter(
    (workbook) => workbook.classroomGrammar.stageKey === selectedStage?.key,
  );
  const recommendationWorkbook =
    selectedStageWorkbooks.find((workbook) => !workbook.studiedAt) ?? workbooks.find((workbook) => !workbook.studiedAt);
  const studyTargetWorkbook = recommendationWorkbook ?? selectedStageWorkbooks[0] ?? workbooks[0];
  const firstStageGrammar = selectedStage?.topics[0]?.grammars[0] ?? null;
  const recommendationGrammar = recommendationWorkbook?.classroomGrammar ?? firstStageGrammar;
  const studiedCount = selectedStageWorkbooks.filter((workbook) => !!workbook.studiedAt).length;
  const isAllStudied = workbooks.length > 0 && !workbooks.some((workbook) => !workbook.studiedAt);
  const stageGrammarCount = selectedStage?.topics.reduce((count, topic) => count + topic.grammars.length, 0) ?? 0;

  // 문법 클릭 → 학습 화면
  const handleGrammarClick = (topicKey: string, grammar: IGrammarItem) => {
    navigate(
      `${IYAGIGO_PATH.CLASSROOM_GRAMMAR_PATH}?language=${languageCode.toLowerCase()}&stage=${selectedStage?.key}&topic=${topicKey}&grammar=${grammar.grammarKey}`,
    );
  };

  const handleStageSelectClick = () => {
    dispatch(modalActions.showDialogModal({ type: 'grammar-stage-select' }));
  };

  const handleRecommendedStudy = () => {
    const grammar = studyTargetWorkbook?.classroomGrammar ?? firstStageGrammar;
    if (!grammar) return;
    navigate(
      `${IYAGIGO_PATH.CLASSROOM_GRAMMAR_PATH}?language=${languageCode.toLowerCase()}&stage=${grammar.stageKey}&topic=${grammar.topicKey}&grammar=${grammar.grammarKey}`,
    );
  };

  const Topics = (selectedStage?.topics ?? []).map((topic) => (
    <TopicCard
      key={uuid4()}
      topic={topic}
      workbooks={workbooks}
      onGrammarClick={(grammar) => handleGrammarClick(topic.key, grammar)}
    />
  ));

  return (
    <>
      <BannerContainer>
        <ImageBlock src={otterTestImage} alt='otter' design={{ image: { height: '80px' } }} />
        <BannerContent>
          <TextBlock text='현재 단계' design={{ text: { size: 'medium', color: color.BLACK_2 } }} />
          <TextBlock text={selectedStageLabel} design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }} />
          <IconTextBlock
            icon={'edit'}
            text='단계 선택하기'
            arrangement='text-icon'
            design={{
              text: { size: 'tiny', color: color.GRAY_1, underline: false },
              icon: { size: 'tiny', color: color.GRAY_1, kind: 'outlined' },
              box: { event: { cursor: 'pointer' }, gap: '2px' },
            }}
            click={{ handler: handleStageSelectClick }}
          />
        </BannerContent>
      </BannerContainer>
      <Gap height={size.MARGIN_2} />
      <TextBlock
        text='오늘의 학습'
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true }, box: { alignX: 'left' } }}
      />
      <Gap height={size.MARGIN_1} />
      <TodayStudyContainer>
        <TextBlock
          text={
            isWorkbookLoading
              ? '학습 정보를 불러오고 있어요'
              : isAllStudied
                ? '모든 문법을 학습했어요'
                : recommendationGrammar
                  ? `<strong>${recommendationGrammar.grammarName?.ko ?? '문법'}</strong>부터 시작해요`
                  : '문법을 시작해 보세요'
          }
          design={{ text: { size: 'large', color: color.BLACK_2, bold: false }, box: { alignX: 'left' } }}
        />
        <Gap height='5px' />
        <TextBlock
          text={recommendationGrammar?.read?.notation ?? ''}
          design={{ text: { size: 'medium', color: color.GRAY_1 }, box: { alignX: 'left' } }}
        />
        <Gap height={size.MARGIN_1} />
        <ProgressLabelContainer>
          <TextBlock text='전체 진도' design={{ text: { size: 'small', color: color.BLACK_4, bold: true }, box: {} }} />
          <TextBlock
            text={`${studiedCount} / ${selectedStageWorkbooks.length || stageGrammarCount}개`}
            design={{ text: { size: 'tiny', color: color.GRAY_1 }, box: {} }}
          />
        </ProgressLabelContainer>
        <Gap height='5px' />
        <ProgressBar
          ratio={
            (selectedStageWorkbooks.length || stageGrammarCount) > 0
              ? studiedCount / (selectedStageWorkbooks.length || stageGrammarCount)
              : 0
          }
          theme={ETheme.BROWN}
          width='100%'
          height='6px'
        />
        <Gap height={size.MARGIN_1} />
        <TextButton
          text={isAllStudied ? '다시 학습하기' : '시작하기'}
          theme={ETheme.BROWN}
          size='medium'
          shape='pill'
          width='100%'
          disabled={isWorkbookLoading || !recommendationGrammar}
          handleClick={handleRecommendedStudy}
        />
      </TodayStudyContainer>
      <Gap height={size.MARGIN_2} />
      <TopicCardsContainer>{Topics}</TopicCardsContainer>
    </>
  );
};

export default GrammarHome;
